/**
 * SRES · Fence Diagram Picker — CP-facing wizard step (Iter109 · Feb 2026).
 *
 * The Competent Person picks any ≥ 3 holes that share a coherent
 * geological story and this component:
 *   1. Loads the roster of holes from
 *      `GET /api/projects/{pid}/cpr/fence-diagram`
 *   2. Live-previews the fence diagram via
 *      `POST /api/projects/{pid}/cpr/fence-diagram/preview` after any
 *      selection change (debounced 400 ms)
 *   3. Persists the selection with
 *      `POST /api/projects/{pid}/cpr/fence-diagram/save` — the next
 *      CPR compile automatically appends the fence appendix.
 *
 * A DELETE removes the persisted selection (fence appendix stops being
 * emitted on subsequent compiles).
 *
 * The component is mounted inside the CleanCPRDashboard as a new
 * SECTION E ("Fence Diagram Appendix"), sitting below the CPR run
 * governance panel.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Path, ArrowsClockwise, CheckCircle, Trash, MapPin, WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";

const backendURL = process.env.REACT_APP_BACKEND_URL;


function FenceHoleRow({ hole, index, selected, disabled, onToggle }) {
  return (
    <li className={`flex items-center gap-3 px-2 py-1.5 rounded-md text-[12.5px]
                        ${disabled
                            ? "bg-slate-50 text-slate-400 cursor-not-allowed"
                            : selected
                              ? "bg-brand-blue/5 border border-brand-blue/30"
                              : "hover:bg-slate-50 border border-transparent"}`}
        data-testid={`fence-hole-row-${hole.hole_id}`}>
      <Checkbox checked={selected}
                    disabled={disabled}
                    onCheckedChange={() => onToggle(hole.hole_id)}
                    data-testid={`fence-hole-checkbox-${hole.hole_id}`} />
      <span className="w-6 text-[10.5px] font-mono text-slate-400">
        {index != null ? String(index + 1).padStart(2, "0") : "--"}
      </span>
      <span className="flex-1 font-mono font-semibold text-slate-800">
        {hole.hole_id}
      </span>
      {hole.has_coords ? (
        <span className="text-[10.5px] font-mono text-slate-500">
          RL {hole.z != null ? hole.z.toFixed(0) : "—"} · EOH{" "}
          {hole.depth != null ? hole.depth.toFixed(0) : "—"} m
        </span>
      ) : (
        <span className="text-[10.5px] text-amber-600 flex items-center gap-1">
          <WarningCircle size={11} /> no collar
        </span>
      )}
    </li>
  );
}


export default function FenceDiagramPicker({ pid }) {
  const [roster, setRoster]           = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [title, setTitle]             = useState("");
  const [previewPng, setPreviewPng]   = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [error, setError]             = useState(null);
  const previewTimer                  = useRef(null);
  const previewAbort                  = useRef(null);

  // ── Load the roster + any saved selection on mount ──────────────
  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api.get(`/projects/${pid}/cpr/fence-diagram`)
        .then((res) => res.data);
      setRoster(r);
      const saved = r?.selection?.hole_ids || [];
      setSelectedIds(saved);
      setTitle(r?.selection?.title || "");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Load failed");
    }
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const holesById = useMemo(() => {
    const m = {};
    for (const h of roster?.holes || []) m[h.hole_id] = h;
    return m;
  }, [roster]);

  // ── Debounced live preview ─────────────────────────────────────
  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    if (previewAbort.current) previewAbort.current.abort();
    if (selectedIds.length < 3) {
      setPreviewPng(null);
      return;
    }
    setPreviewLoading(true);
    previewTimer.current = setTimeout(async () => {
      const ac = new AbortController();
      previewAbort.current = ac;
      try {
        // Use axios directly so we can request a PNG blob (api.js is JSON-only)
        const token = localStorage.getItem("sres_token");
        const res = await axios.post(
          `${backendURL}/api/projects/${pid}/cpr/fence-diagram/preview`,
          { hole_ids: selectedIds, title: title || null },
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            responseType: "blob",
            signal: ac.signal,
          },
        );
        const url = URL.createObjectURL(res.data);
        setPreviewPng((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      } catch (e) {
        if (e?.code === "ERR_CANCELED") return;
        // Blob error → fetch as text to show a friendly message
        if (e?.response?.data instanceof Blob) {
          try {
            const txt = await e.response.data.text();
            const parsed = JSON.parse(txt);
            toast.error(parsed?.detail?.message || "Preview failed");
          } catch { toast.error("Preview failed"); }
        } else {
          toast.error(e?.message || "Preview failed");
        }
        setPreviewPng(null);
      } finally { setPreviewLoading(false); }
    }, 400);
    return () => {
      if (previewTimer.current) clearTimeout(previewTimer.current);
      if (previewAbort.current) previewAbort.current.abort();
    };
  }, [selectedIds, title, pid]);

  const toggle = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }, []);

  const clearAll = () => { setSelectedIds([]); };

  const selectAllWithCoords = () => {
    setSelectedIds((roster?.holes || [])
      .filter((h) => h.has_coords)
      .map((h) => h.hole_id));
  };

  const handleSave = async () => {
    if (selectedIds.length < 3) {
      toast.error("Fence diagram needs at least 3 holes.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(
        `/projects/${pid}/cpr/fence-diagram/save`,
        { hole_ids: selectedIds, title: title || null },
      );
      toast.success(
        `Fence diagram saved · ${r.data.selection.hole_ids.length} holes · will appear as an appendix in the next CPR compile.`
      );
      setRoster((prev) => prev ? { ...prev, selection: r.data.selection } : prev);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                     || e?.response?.data?.detail
                     || e.message || "Save failed");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/projects/${pid}/cpr/fence-diagram`);
      toast.success("Fence diagram cleared — CPR compile will no longer append it.");
      setSelectedIds([]); setTitle("");
      setRoster((prev) => prev
                              ? { ...prev, selection: { hole_ids: [], title: null } }
                              : prev);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Delete failed");
    } finally { setDeleting(false); }
  };

  if (error)
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-[12.5px] text-red-700"
               data-testid="fence-diagram-picker-error">
        Failed to load fence diagram roster: {String(error)}
      </div>
    );
  if (!roster)
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4 text-[12.5px] text-slate-500 italic"
               data-testid="fence-diagram-picker-loading">
        Loading fence diagram picker…
      </div>
    );

  const eligible = roster.eligible_count || 0;
  const savedCount = roster.selection?.hole_ids?.length || 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden"
             data-testid="fence-diagram-picker">
      {/* Header */}
      <div className="flex items-start gap-3 p-4 border-b border-slate-100">
        <div className="w-9 h-9 rounded-lg bg-brand-blue/10 flex items-center justify-center shrink-0">
          <Path size={18} className="text-brand-blue" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10.5px] uppercase tracking-[0.28em] text-slate-500">
            SRES · CPR Appendix
          </div>
          <h2 className="font-heading font-bold text-[16px] tracking-tight text-slate-900 leading-tight">
            Fence Diagram Picker
          </h2>
          <p className="text-[12px] text-slate-600 mt-0.5">
            Pick any 3+ holes that share a coherent geological story. The
            fence diagram appears as a dedicated appendix in the next CPR
            compile — same swim-lane engine as the per-hole logs, one
            source of truth for lithology colours.
          </p>
        </div>
        <div className="text-right text-[11px] text-slate-500 shrink-0">
          <div><b>{eligible}</b> hole{eligible === 1 ? "" : "s"} with collars</div>
          {savedCount >= 3 && (
            <div className="text-emerald-700 mt-0.5"
                     data-testid="fence-diagram-saved-badge">
              ✓ Saved · {savedCount} holes
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:divide-x lg:divide-slate-100">
        {/* LEFT — picker + save controls */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <input
              type="text"
              className="flex-1 border border-slate-200 rounded px-2 py-1 text-[12px]"
              placeholder="Optional title (e.g. NE-SW fence · main lode)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="fence-diagram-title"
            />
          </div>
          <div className="flex items-center gap-2 mb-2 text-[11px]">
            <button type="button"
                        onClick={selectAllWithCoords}
                        className="text-brand-blue hover:underline"
                        data-testid="fence-diagram-select-all">
              Select all
            </button>
            <span className="text-slate-300">·</span>
            <button type="button"
                        onClick={clearAll}
                        className="text-slate-500 hover:underline"
                        data-testid="fence-diagram-clear-all">
              Clear
            </button>
            <span className="ml-auto text-slate-500">
              <span className={selectedIds.length >= 3
                                    ? "text-emerald-700 font-semibold"
                                    : "text-amber-600 font-semibold"}
                        data-testid="fence-diagram-selected-count">
                {selectedIds.length}
              </span>{" "}
              selected · minimum 3
            </span>
          </div>
          <ul className="space-y-0.5 max-h-[380px] overflow-y-auto pr-1"
                  data-testid="fence-diagram-hole-list">
            {(roster.holes || []).map((h) => {
              const idx = selectedIds.indexOf(h.hole_id);
              return (
                <FenceHoleRow key={h.hole_id}
                                  hole={h}
                                  index={idx >= 0 ? idx : null}
                                  selected={idx >= 0}
                                  disabled={!h.has_coords}
                                  onToggle={toggle} />
              );
            })}
            {(roster.holes || []).length === 0 && (
              <li className="text-[12px] text-slate-500 italic py-3 text-center">
                No collar records ingested for this project yet.
              </li>
            )}
          </ul>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm"
                        onClick={handleSave}
                        disabled={saving || selectedIds.length < 3}
                        className="bg-brand-blue text-white hover:opacity-90 text-[12px]"
                        data-testid="fence-diagram-save-btn">
              <CheckCircle size={13} className="mr-1" />
              {saving ? "Saving…" : "Save · include in next CPR"}
            </Button>
            {savedCount >= 3 && (
              <Button size="sm" variant="outline"
                          onClick={handleDelete}
                          disabled={deleting}
                          className="text-slate-600 text-[12px]"
                          data-testid="fence-diagram-delete-btn">
                <Trash size={13} className="mr-1" />
                {deleting ? "Removing…" : "Remove"}
              </Button>
            )}
          </div>
        </div>

        {/* RIGHT — live preview */}
        <div className="p-4 bg-slate-50">
          <div className="text-[10.5px] uppercase tracking-[0.28em] text-slate-500 mb-1.5 flex items-center gap-2">
            <MapPin size={12} />
            Live preview
            {previewLoading && (
              <ArrowsClockwise size={11}
                                     className="animate-spin text-slate-400 ml-1" />
            )}
          </div>
          {selectedIds.length < 3 ? (
            <div className="h-[280px] flex items-center justify-center rounded-md border border-dashed border-slate-300 bg-white text-[12px] text-slate-400"
                     data-testid="fence-diagram-preview-empty">
              Select at least 3 holes to preview the fence.
            </div>
          ) : previewPng ? (
            <img src={previewPng}
                     alt="Fence diagram preview"
                     className="w-full rounded-md border border-slate-200 bg-white"
                     data-testid="fence-diagram-preview-img" />
          ) : (
            <div className="h-[280px] flex items-center justify-center rounded-md border border-slate-200 bg-white text-[12px] text-slate-400"
                     data-testid="fence-diagram-preview-placeholder">
              Rendering fence…
            </div>
          )}
          {selectedIds.length >= 3 && (
            <div className="mt-2 text-[10.5px] text-slate-500 font-mono"
                     data-testid="fence-diagram-preview-order">
              Order: {selectedIds.map((id, i) => (
                <span key={id}>
                  {i > 0 && " → "}
                  <span className="text-slate-700">{id}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
